import { FileOpener } from '@capacitor-community/file-opener';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { isNativeMobile } from '@vestwell-frontend/helpers';

import axios from 'axios';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useBearerToken } from './useBearerToken';
import { useNotifications } from './useNotifications';

const blobToBase64DataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

function triggerFileDownload(data: string, name: string) {
  const link = document.createElement('a');
  link.href = data;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const openFileOnMobile = async (uri: string, mimeType: string) => {
  try {
    await FileOpener.open({
      contentType: mimeType,
      filePath: uri,
      openWithDefault: true
    });
  } catch (error) {
    // Handle specific Android error for no activity found
    if (error.message?.includes('Activity not found')) {
      const fileType = mimeType.split('/').pop()?.split('.').pop() || 'file';
      throw new Error(
        `No app found to open this ${fileType} file. Please install a compatible app from the Play Store to view this file.`
      );
    }
    throw error;
  }
};

export const useDownload = (
  pathname: string,
  avoidPreview: boolean = false
) => {
  const [fileUri, setFileUri] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileApp, setIsMobileApp] = useState<boolean | null>(null);

  const [token, , isTokenLoading] = useBearerToken();

  const notify = useNotifications();

  const tokenRef = useRef(token);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    isNativeMobile().then(setIsMobileApp);
  });

  return useCallback(
    async (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();

      // If already loading or data exists, prevent further action
      if (isLoading) {
        notify.info('Document is already being downloaded.');
        return;
      }

      setIsLoading(true);

      let currentToken = tokenRef.current;
      if (process.env.NODE_ENV !== 'test') {
        if (!currentToken || isTokenLoading) {
          try {
            let isWaiting = false;
            const waitForToken = new Promise<string>((resolve, reject) => {
              const timeout = setTimeout(() => {
                reject(new Error('Token request timed out after 10 seconds'));
              }, 10000);

              const checkToken = async () => {
                const latestToken = tokenRef.current;
                if (latestToken && !isWaiting) {
                  isWaiting = true;
                  clearTimeout(timeout);
                  resolve(latestToken);
                } else if (!isTokenLoading && !latestToken) {
                  clearTimeout(timeout);
                  reject(
                    new Error('Token not available - please log in again')
                  );
                } else {
                  setTimeout(checkToken, 100);
                }
              };
              checkToken();
            });

            currentToken = await waitForToken;
          } catch (error) {
            setIsLoading(false);
            notify.error(
              `Unable to authenticate: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
            return;
          }
        }

        if (!currentToken) {
          setIsLoading(false);
          notify.error('Authentication failed. Please log in and try again.');
          return;
        }
      }

      if (fileUri) {
        if (!isMobileApp) {
          if (avoidPreview) {
            triggerFileDownload(fileUri, fileName); // For web, download existing document
          } else {
            window.open(fileUri, '_blank'); // For web, open existing document
          }
        } else {
          try {
            // For mobile, re-open the file if already downloaded
            await FileOpener.open({
              filePath: fileUri,
              openWithDefault: true
            });
          } catch (error) {
            console.error('Error re-opening file on mobile', error);
            notify.error('Error re-opening file');
          }
        }
        return;
      }

      notify.info(
        'The document is being prepared for download. This may take a few seconds.'
      );

      try {
        const response = await axios.get(pathname, {
          headers: {
            Authorization: currentToken
          },
          responseType: 'blob'
        });

        const blob = response.data;
        const contentDisposition = response.headers['content-disposition'];
        const downloadedFileName = contentDisposition
          ? contentDisposition
              .split('filename=')[1]
              ?.split(';')[0]
              ?.replace(/"/g, '')
          : `${new Date().getTime()}.${getFileExtension(blob.type)}`;

        setFileName(downloadedFileName);

        if (!isMobileApp) {
          if (avoidPreview) {
            // Web: just download document
            const dataUrl = await blobToBase64DataUrl(blob);
            setFileUri(dataUrl);
            triggerFileDownload(dataUrl, downloadedFileName);
          } else {
            // Web: Open the blob as a new URL
            const blobUrl = window.URL.createObjectURL(blob);
            setFileUri(blobUrl);
            window.open(blobUrl, '_blank'); // open in new tab
          }
        } else {
          // Mobile: Save file and open with default app
          const dataUrl = await blobToBase64DataUrl(blob);

          const savedFile = await Filesystem.writeFile({
            data: dataUrl,
            directory: Directory.Documents,
            path: downloadedFileName,
            recursive: true
          });

          setFileUri(savedFile.uri); // Store file URI for reuse

          try {
            await openFileOnMobile(savedFile.uri, blob.type);
          } catch (error) {
            notify.error(error.message);
            // Even if opening fails, we've still downloaded the file
            notify.info('File has been downloaded to your documents folder');
          }
        }
      } catch (error) {
        console.error('Failed to fetch document', error);
        notify.error(
          `Failed to fetch document: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      } finally {
        setIsLoading(false);
      }
    },
    [fileUri, isLoading, isMobileApp, pathname, avoidPreview, isTokenLoading]
  );
};

const getFileExtension = (mimeType: string) => {
  switch (mimeType) {
    case 'application/pdf':
      return 'pdf';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'xlsx';
    default:
      return mimeType.split('/')[1];
  }
};
